<template>
  <div class="portfolio-view">
    <v-fade-transition>
      <div v-if="loading" key="state-loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-else-if="!loading" key="state-content" class="position-relative">
        <!-- Edit Button for Owner -->
        <!-- Menu for Owner -->


        <template v-if="portfolio && !isEmpty">
          <!-- Bio & Block Sections -->
          <section v-if="portfolio.bio || portfolio.sections?.length > 0" key="sec-bio" class="mb-10">
            <div 
              v-if="portfolio.bio" 
              class="text-body-1 mb-6 text-grey-lighten-2" 
              style="white-space: pre-wrap;" 
              v-html="renderRichText(portfolio.bio)"
              @click="handleContentClick"
            ></div>
            
            <div v-for="(section, index) in portfolio.sections" :key="'sec-block-' + index" class="mb-4">
              <h1 v-if="section.type === 'h1'" class="text-h4 font-weight-black mb-4">{{ section.content }}</h1>
              <h2 v-else-if="section.type === 'h2'" class="text-h5 font-weight-bold mb-3 text-primary">{{ section.content }}</h2>
              <div 
                v-else-if="section.type === 'text'" 
                class="text-body-1 mb-4 text-grey-lighten-2" 
                style="white-space: pre-wrap;" 
                v-html="renderRichText(section.content)"
                @click="handleContentClick"
              ></div>
              <v-img 
                v-else-if="section.type === 'image'" 
                :src="resolveImageUrl(section)" 
                class="rounded-lg mb-4 border border-white-05" 
                style="max-height: 400px; width: auto;"
              ></v-img>
            </div>
          </section>

          <!-- Ludography (Game Projects) -->
          <section v-if="portfolio.ludography?.length > 0" key="sec-games" class="mb-10">
            <h2 class="text-h5 font-weight-black mb-6 d-flex align-center">
              <v-icon icon="mdi-controller-classic" class="mr-3" color="primary"></v-icon>
              Ludography
            </h2>
            <v-row>
              <v-col v-for="(game, index) in portfolio.ludography" :key="index" cols="12">
                <v-card variant="outlined" class="game-card rounded-xl border border-white-05 bg-black-05 overflow-hidden" :href="game.link" target="_blank">
                  <div class="d-flex flex-column flex-sm-row" style="min-height: 250px; overflow: hidden;">
                  <div style="width: 200px; flex-shrink: 0; background: #1a1a1a; position: relative; min-height: 250px;">
                      <v-img 
                        v-if="resolveGameThumbnail(game)" 
                        :src="resolveGameThumbnail(game)" 
                        style="width: 100%; height: 100%; position: absolute; top: 0; left: 0;"
                        cover
                        loading="eager"
                      >
                        <template v-slot:placeholder>
                          <div class="d-flex align-center justify-center fill-height bg-dark">
                            <v-icon icon="mdi-image-off-outline" color="grey"></v-icon>
                          </div>
                        </template>
                      </v-img>
                      <div v-else class="d-flex align-center justify-center font-weight-black fill-height px-4 text-center bg-dark" style="opacity: 0.6;">
                        {{ game.title }}
                      </div>
                    </div>

                    <!-- Content (Responsive & Wrapping) -->
                    <div style="flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; padding: 24px;">
                      <div class="mb-2" style="width: 100%;">
                        <div 
                          class="text-h5 font-weight-black" 
                          style="white-space: normal; line-height: 1.2; word-break: break-word; overflow-wrap: anywhere; word-wrap: break-word;"
                        >
                          {{ game.title }}
                        </div>
                      </div>
                      <div class="mb-2" style="width: 100%;">
                        <div 
                          class="text-subtitle-1 text-primary font-weight-bold"
                          style="white-space: normal; opacity: 1; word-break: break-word; overflow-wrap: anywhere; word-wrap: break-word;"
                        >
                          {{ game.role }} · {{ game.year }}
                        </div>
                      </div>
                      <div v-if="game.platform" style="width: 100%;">
                        <div 
                          class="text-body-1 text-grey-lighten-1"
                          style="white-space: normal; word-break: break-word; overflow-wrap: anywhere; word-wrap: break-word;"
                        >
                          {{ game.platform }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </section>

          <!-- Experiences -->
          <section v-if="portfolio.experiences?.length > 0" key="sec-exp" class="mb-10">
            <h2 class="text-h5 font-weight-black mb-6 d-flex align-center">
              <v-icon icon="mdi-briefcase-variant" class="mr-3" color="primary"></v-icon>
              Experience
            </h2>
            <div class="experience-timeline">
              <div v-for="(exp, index) in portfolio.experiences" :key="index" class="exp-item mb-12 pl-6 border-l-2 border-primary position-relative py-4">
                <div class="exp-dot bg-primary"></div>
                <div class="d-flex flex-wrap justify-space-between align-start mb-1">
                  <h3 class="text-h6 font-weight-bold">{{ exp.position }}</h3>
                  <span class="text-caption text-grey font-weight-bold bg-black-05 px-2 py-1 rounded">
                    {{ exp.startDate }} — {{ exp.current ? 'Present' : exp.endDate }}
                  </span>
                </div>
                <div class="text-subtitle-2 text-primary mb-2 font-weight-bold">{{ exp.company }}</div>
                <div class="text-body-2 text-grey-lighten-1" style="white-space: pre-wrap;">{{ exp.description }}</div>
              </div>
            </div>
          </section>

        </template>

        <!-- Empty States -->
        <template v-if="isEmpty">
          <!-- Own Profile Empty State -->
          <div v-if="isOwnProfile" key="empty-own" class="text-center py-12 border rounded-xl border-dashed border-white-05">
            <v-icon icon="mdi-account-details-outline" size="64" color="grey-darken-2" class="mb-4"></v-icon>
            <h3 class="text-h5 text-grey">Your portfolio is empty</h3>
            <p class="text-body-2 text-grey-darken-1 mt-2 mb-6">Create a professional gamedev profile to showcase your work!</p>
            <v-btn color="primary" rounded="pill" @click="$emit('edit')">Setup Portfolio</v-btn>
          </div>

          <!-- Visitor Empty State -->
          <div v-else key="empty-visitor" class="text-center py-12 border rounded-xl border-dashed border-white-05 opacity-50">
            <v-icon icon="mdi-account-details-outline" size="64" color="grey-darken-2" class="mb-4"></v-icon>
            <h3 class="text-h6 text-grey">No gamedev portfolio set up yet</h3>
            <p class="text-caption text-grey mt-2">Check back later or follow for updates!</p>
          </div>
        </template>
      </div>
      <div v-else key="state-none" class="text-center py-12">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
    </v-fade-transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { getPortfolio, Portfolio, PortfolioSection, getPdsEndpoint } from '@/services/bluesky';
import { renderRichText, handleRichTextClick } from '@/utils/richtext';
import { useRouter } from 'vue-router';

const props = defineProps<{
  userDid?: string;
  isOwnProfile?: boolean;
}>();

const router = useRouter();

const emit = defineEmits(['edit']);

const portfolio = ref<Portfolio | null>(null);
const loading = ref(true);
const pdsEndpoint = ref<string | null>(null);

const resolveImageUrl = (section: PortfolioSection) => {
  if (section.blob) {
    const cid = section.blob.ref?.toString() || section.blob.cid;
    const mime = section.blob.mimeType || section.blob.mime_type;
    
    if (cid && props.userDid) {
      const isGif = mime === 'image/gif' || (section.content && section.content.toLowerCase().endsWith('.gif'));
      
      // Direct PDS link for GIFs often works better than CDN conversion
      if (isGif && pdsEndpoint.value) {
        return `${pdsEndpoint.value}/xrpc/com.atproto.sync.getBlob?did=${props.userDid}&cid=${cid}`;
      }
      
      const format = isGif ? '@gif' : '@jpeg';
      return `https://cdn.bsky.app/img/feed_fullsize/plain/${props.userDid}/${cid}${format}`;
    }
  }
  return section.content;
};

const isEmpty = computed(() => {
  if (!portfolio.value) return true;
  return !portfolio.value.bio && 
         portfolio.value.sections?.length === 0 && 
         portfolio.value.experiences?.length === 0 && 
         portfolio.value.ludography?.length === 0;
});

const refresh = async () => {
  portfolio.value = null; // Added this line as per instruction
  if (!props.userDid) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const [portfolioData, pdsEndpointData] = await Promise.all([
      getPortfolio(props.userDid),
      getPdsEndpoint(props.userDid)
    ]);
    portfolio.value = portfolioData;
    pdsEndpoint.value = pdsEndpointData;
  } catch (error) {
    console.error("Failed to load portfolio or PDS endpoint:", error);
    portfolio.value = null; // Or handle error state appropriately
  } finally {
    loading.value = false;
  }
};

onMounted(refresh);

watch(() => props.userDid, refresh);

const resolveGameThumbnail = (game: any) => {
  if (game.blob) {
    const cid = game.blob.ref?.toString() || game.blob.cid;
    if (cid && props.userDid) {
      return `https://cdn.bsky.app/img/feed_fullsize/plain/${props.userDid}/${cid}@jpeg`;
    }
  }
  return game.thumbnail;
};

const handleContentClick = (event: MouseEvent) => {
  handleRichTextClick(event, { 
    router
  });
};

// Expose refresh method
defineExpose({ refresh });
</script>

<style scoped>
.portfolio-view {
  animation: fadeIn 0.4s ease-out;
  max-width: 900px;
  margin: 0 auto;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.text-grey-lighten-2 {
  color: #BDBDBD !important;
  line-height: 1.6;
}

.bg-black-05 {
  background: rgba(255, 255, 255, 0.03) !important;
  backdrop-filter: blur(10px);
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.max-height-400 {
  max-height: 400px;
}

.game-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.02) !important;
}

.game-card:hover {
  transform: translateY(-6px);
  border-color: var(--v-primary-base) !important;
  background-color: rgba(var(--v-theme-primary), 0.08) !important;
  box-shadow: 0 12px 24px -10px rgba(0, 0, 0, 0.5);
}

.experience-timeline {
  position: relative;
  padding-left: 4px;
}

.exp-item {
  position: relative;
  transition: all 0.3s ease;
}

.exp-item:hover {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  margin-left: -8px;
  padding-left: 32px !important;
}

.exp-dot {
  position: absolute;
  left: -2px;
  top: 10px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  transform: translateX(-50%);
  border: 2px solid #000;
  z-index: 1;
}

.border-l-2 {
  border-left-width: 2px !important;
  border-left-style: solid;
}

.gap-2 { gap: 8px; }
.gap-4 { gap: 16px; }

.border-dashed {
  border-style: dashed !important;
}

.section-title {
  letter-spacing: -0.5px;
}

.min-width-0 {
  min-width: 0;
}
</style>
